import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, CheckCircle2 } from "lucide-react";
import { PROVINCES } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full rounded-lg border border-[#D6D6D6] bg-white px-4 py-3.5 font-body text-sm text-[#1A1A1A] placeholder:text-[#B5B0A6] outline-none transition-colors focus:border-[#F8B808]";

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    province: "",
    message: "",
    inquiry_type: "general",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, { ...form, source: "Homepage Contact Form" });
      setDone(true);
      toast.success("Thank you! We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", province: "", message: "", inquiry_type: "general" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-[35px] lg:py-[50px] bg-[#FFFFFF]" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-[38px] lg:gap-20 items-start">
          <div className="lg:sticky lg:top-28">
            <h2 className="font-heading text-[31px] sm:text-5xl lg:text-[50px] font-medium tracking-tight text-[#1A1A1A] leading-[1.04]">
              Request Our Catalog
            </h2>
            <p className="font-body text-lg md:text-xl text-[#6B6B66] mt-[19px] lg:mt-[41px] leading-relaxed max-w-lg">
              Request our latest catalog to explore current product categories,
              featured items and available inventory.
            </p>

            <div className="mt-8 lg:mt-10 space-y-5">
              {[
                { icon: Mail, label: "Email", value: "info@homecart.ca" },
              ].map((c) => (
                <div key={c.label} className="flex items-center gap-4">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#FBF4DD]">
                    <c.icon className="h-5 w-5 text-[#1A1A1A]" />
                  </span>
                  <div>
                    <p className="font-body text-xs font-semibold uppercase tracking-wide text-[#9A958C]">{c.label}</p>
                    <p className="font-heading text-base font-semibold text-[#1A1A1A]">{c.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-[8px] bg-white border border-[#E5E5E5] p-7 sm:p-10">
            {done ? (
              <div className="flex flex-col items-center text-center py-16" data-testid="contact-success">
                <CheckCircle2 className="h-16 w-16 text-[#F8B808]" />
                <h3 className="font-heading text-2xl font-bold text-[#1F2937] mt-5">Request Received</h3>
                <p className="font-body text-[#6B7280] mt-3 max-w-sm">
                  Thank you for reaching out to Home Cart. A member of our team will get back to you soon.
                </p>
                <button
                  onClick={() => setDone(false)}
                  data-testid="send-another"
                  className="mt-7 font-body text-sm font-bold text-[#F8B808] hover:underline"
                >
                  Send another request
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="font-body text-sm font-semibold text-[#1F2937] mb-1.5 block">Name</label>
                    <input
                      data-testid="input-name"
                      className={inputClass}
                      placeholder="Your full name"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="font-body text-sm font-semibold text-[#1F2937] mb-1.5 block">Email</label>
                    <input
                      data-testid="input-email"
                      type="email"
                      className={inputClass}
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="font-body text-sm font-semibold text-[#1F2937] mb-1.5 block">Phone</label>
                    <input
                      data-testid="input-phone"
                      className={inputClass}
                      placeholder="(000) 000-0000"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="font-body text-sm font-semibold text-[#1F2937] mb-1.5 block">Province</label>
                    <select
                      data-testid="input-province"
                      className={`${inputClass} appearance-none`}
                      value={form.province}
                      onChange={(e) => update("province", e.target.value)}
                    >
                      <option value="">Select a province</option>
                      {PROVINCES.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="font-body text-sm font-semibold text-[#1F2937] mb-1.5 block">Message</label>
                  <textarea
                    data-testid="input-message"
                    rows={4}
                    className={`${inputClass} resize-none`}
                    placeholder="Tell us how we can help..."
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-[5px] bg-[#F8B808] px-7 h-10 font-body text-base font-medium text-[#1A1A1A] transition-all hover:bg-[#DD9F00] active:scale-[0.98] disabled:opacity-60"
                >
                  {loading ? "Sending..." : "Request Catalog"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
